import React, { useEffect, useState, useRef } from 'react';
import { Clock, CheckCircle2, Camera, AlertCircle, Loader2, ImagePlus, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import NotificationsFeed from '../../components/NotificationsFeed';
import { photoErrorInfo } from '../../lib/uploadErrors';
import { compressPhoto, UPLOAD_TIMEOUT_MS } from '../../lib/photo';
import { isGtFullServiceDay } from './PriceEntry';
import { HappyHourCards } from '../../components/HappyHourCards';

const PRODUCT_LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const PRODUCT_ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];

const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// prev official price puede ser {self, full} o número legado (→ self)
const normPrev = (v) => (v == null ? null : (typeof v === 'object' ? v : { self: v, full: null }));
const priceDelta = (nv, pv) => {
  if (nv == null || pv == null) return null;
  const n = Number(nv), p = Number(pv);
  if (!Number.isFinite(n) || !Number.isFinite(p) || p <= 0) return null;
  const d = +(n - p).toFixed(2);
  return Math.abs(d) < 0.005 ? 0 : d;
};

const useCountdown = (targetIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000 * 30); return () => clearInterval(t); }, []);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - now;
  const s = Math.floor(Math.abs(diff) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return { diff, text: diff > 0 ? `en ${h}h ${m}m` : `hace ${h}h ${m}m` };
};

const PushCard = ({ sp, onRefresh }) => {
  const photoInput = useRef(null);
  const galleryInput = useRef(null);
  const [loading, setLoading] = useState(false);
  const countdown = useCountdown(sp.station_effective_at);
  const canReceive = countdown && countdown.diff <= 2 * 60 * 60 * 1000 && !sp.received_at;
  const canPhoto = sp.received_at && !sp.confirmed_at;

  const doReceive = async () => {
    setLoading(true);
    try { await api.post(`/station-pushes/${sp.id}/receive`); toast.success('Recepción confirmada'); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setLoading(true);
    try {
      const cf = await compressPhoto(f);
      const fd = new FormData(); fd.append('photo', cf);
      await api.post(`/station-pushes/${sp.id}/confirm-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS });
      toast.success('Foto del monolito enviada · precio oficial actualizado');
      onRefresh();
    } catch (e) {
      if (!e.response) {
        toast.error('Conexión lenta o inestable', { description: 'No se pudo subir la foto. Inténtalo de nuevo con mejor señal.', duration: 8000 });
      } else {
        const info = photoErrorInfo(e);
        toast.error(info.title, { description: info.hint, duration: 8000 });
      }
    }
    finally { setLoading(false); }
  };

  const statusBadge = () => {
    if (sp.confirmed_at) return <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#dcfce7] text-[#166534]">Confirmado</span>;
    if (sp.received_at) return <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#dbeafe] text-[#1e40af]">Recibido</span>;
    if (sp.status === 'escalated') return <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b]">Escalado</span>;
    return <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]">Pendiente</span>;
  };

  return (
    <div className="bg-white border border-[#e8e5de] rounded-xl p-4 space-y-3" data-testid={`pending-push-${sp.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[15px] font-medium leading-tight">{sp.station_name}</p>
          <p className="text-[11.5px] text-[#8a8579]">{sp.brand} · push por {sp.push_created_by_name}</p>
        </div>
        {statusBadge()}
      </div>

      <div className="bg-[#f3f0e9] border border-[#e8e5de] rounded-md p-2.5 text-[12px]">
        <div className="flex items-center gap-1.5 text-[#1c1c1c]">
          <Clock size={12}/>
          <span className="font-medium">Efectivo: {fmt(sp.station_effective_at)}</span>
          {countdown && <span className="text-[#8a8579] text-[11px]">({countdown.text})</span>}
        </div>
        {sp.dept_delay_hours > 0 && <p className="text-[10.5px] text-[#8a8579] mt-0.5">Delay depto +{sp.dept_delay_hours} h</p>}
      </div>

      {(sp.push_preset_note_text || sp.push_message) && (
        <div className="bg-[#fafaf6] border border-[#e8e5de] rounded-md p-2.5 text-[11.5px] text-[#3a3a3a] italic">
          {sp.push_preset_note_title && <p className="font-medium not-italic text-[11px] text-[#8a8579] uppercase tracking-wider">{sp.push_preset_note_title}</p>}
          {sp.push_preset_note_text && <p>{sp.push_preset_note_text}</p>}
          {sp.push_message && <p className="mt-1">{sp.push_message}</p>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-[12px]">
        {PRODUCT_ORDER.filter(pid => sp.final_prices && sp.final_prices[pid]).map(pid => {
          const p = sp.final_prices[pid];
          const prev = normPrev(sp.prev_prices?.[pid]);
          const dSelf = priceDelta(p.self, prev?.self);
          const dFull = priceDelta(p.full, prev?.full);
          const changed = (dSelf != null && dSelf !== 0) || (dFull != null && dFull !== 0);
          const noChange = !changed && (dSelf === 0 || dFull === 0);
          const parts = [];
          if (dSelf != null && dSelf !== 0) parts.push(`${dSelf < 0 ? '▼ Baja' : '▲ Sube'} Q${Math.abs(dSelf).toFixed(2)}`);
          if (dFull != null && dFull !== 0 && dFull !== dSelf) parts.push(`Serv. Completo ${dFull < 0 ? '▼' : '▲'} Q${Math.abs(dFull).toFixed(2)}`);
          if (changed && parts.length === 0 && dFull != null && dFull !== 0) parts.push(`${dFull < 0 ? '▼ Baja' : '▲ Sube'} Q${Math.abs(dFull).toFixed(2)}`);
          return (
            <div key={pid} className={`rounded-md p-2 ${changed ? 'border-2 border-[#0f1d6f] bg-[#e7eeff]' : 'border border-[#e8e5de]'}`} data-testid={`push-product-${sp.id}-${pid}`}>
              <div className="flex items-center justify-between gap-1">
                <p className={`text-[10px] uppercase tracking-wider ${changed ? 'text-[#0f1d6f] font-bold' : 'text-[#8a8579]'}`}>{PRODUCT_LABELS[pid] || pid}</p>
                {changed && <span className="text-[8.5px] font-bold px-1.5 py-[1px] rounded bg-[#0f1d6f] text-white uppercase tracking-wider" data-testid={`push-changed-badge-${sp.id}-${pid}`}>Cambia</span>}
              </div>
              <p className="font-medium">Auto: Q{p.self}</p>
              {p.full !== null && p.full !== undefined && <p className="text-[11px] text-[#5a5a5a]">Servicio Completo: Q{p.full}</p>}
              {changed ? (
                <p className="text-[12px] font-bold text-[#0f1d6f] mt-1" data-testid={`push-delta-${sp.id}-${pid}`}>{parts.join(' · ')}</p>
              ) : noChange ? (
                <p className="text-[10px] text-[#8a8579] mt-1" data-testid={`push-nochange-${sp.id}-${pid}`}>= Sin cambio</p>
              ) : null}
            </div>
          );
        })}
      </div>

      {sp.received_at && (
        <p className="text-[11px] text-[#166534] flex items-center gap-1"><CheckCircle2 size={12}/> Recepción confirmada {fmt(sp.received_at)}</p>
      )}
      {sp.confirmed_at && (
        <p className="text-[11px] text-[#166534] flex items-center gap-1"><Camera size={12}/> Foto del monolito enviada {fmt(sp.confirmed_at)}</p>
      )}

      {sp.confirmed_at && sp.ocr_match && Object.keys(sp.ocr_match).length > 0 && (
        <div className="rounded-md border border-[#e8e5de] bg-[#fafaf6] p-2.5" data-testid={`ocr-match-${sp.id}`}>
          <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-1.5">Lector de precios (OCR)</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(sp.ocr_match).map(([k, v]) => {
              const cls = v === 'ok' ? 'bg-[#dcfce7] text-[#166534]' : v === 'mismatch' ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#fef3c7] text-[#92400e]';
              const label = v === 'ok' ? '✓' : v === 'mismatch' ? '×' : '?';
              return (
                <span key={k} className={`text-[10.5px] px-1.5 py-0.5 rounded ${cls}`} data-testid={`ocr-${sp.id}-${k}`}>
                  {label} {k}
                </span>
              );
            })}
          </div>
          {Object.values(sp.ocr_match).some(v => v === 'mismatch') && (
            <p className="text-[10.5px] text-[#991b1b] mt-1.5">⚠ El admin revisará las diferencias detectadas.</p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        {!sp.received_at ? (
          <Button
            onClick={doReceive}
            disabled={!canReceive || loading}
            className={`flex-1 h-10 rounded-full ${canReceive ? 'bg-[#1c1c1c] hover:bg-black text-[#f8f6f1]' : 'bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed'}`}
            data-testid={`btn-receive-${sp.id}`}
          >
            {canReceive ? 'Confirmar recepción' : 'Disponible 2 h antes'}
          </Button>
        ) : !sp.confirmed_at ? (
          <>
            <input ref={photoInput} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid={`input-photo-${sp.id}`}/>
            <input ref={galleryInput} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid={`input-gallery-${sp.id}`}/>
            <Button
              onClick={() => photoInput.current?.click()}
              disabled={loading}
              className="press-soft flex-1 h-10 rounded-full bg-[#15803d] hover:bg-[#166534] text-white"
              data-testid={`btn-photo-${sp.id}`}
            >
              <Camera size={14} className="mr-1.5"/> Tomar foto del monolito
            </Button>
            <Button
              onClick={() => galleryInput.current?.click()}
              disabled={loading}
              variant="outline"
              className="press-soft h-10 rounded-full px-4 border-[#15803d] text-[#15803d] hover:bg-[#dcfce7]"
              title="Subir una foto ya tomada (ej. enviada por el agenciado)"
              data-testid={`btn-gallery-${sp.id}`}
            >
              <ImagePlus size={14} className="mr-1.5"/> Galería
            </Button>
          </>
        ) : (
          <div className="flex-1 text-center text-[12px] text-[#166534] py-2">Cambio oficializado</div>
        )}
      </div>
    </div>
  );
};

const CommCard = ({ c, onAck }) => {
  const [busy, setBusy] = useState(false);
  const copy = async () => {
    const txt = (c.title ? c.title + '\n' : '') + c.body;
    try { await navigator.clipboard.writeText(txt); toast.success('Copiado al portapapeles'); }
    catch { toast.error('No se pudo copiar'); }
  };
  const ack = async () => {
    setBusy(true);
    try { await api.post(`/comms/${c.id}/ack`); onAck(c.id); toast.success('Lectura confirmada'); }
    catch { toast.error('Error al confirmar'); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#eef2ff] border border-[#c7d2fe] rounded-xl p-4 space-y-2.5" data-testid={`comm-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10.5px] uppercase tracking-wider text-[#4338ca] font-medium">Comunicado de Pricing</p>
          {c.title && <p className="text-[14.5px] font-semibold text-[#1e1b4b] mt-0.5">{c.title}</p>}
        </div>
        <span className="text-[10.5px] text-[#6366f1] shrink-0">{fmt(c.created_at)}</span>
      </div>
      <p className="text-[13px] text-[#312e81] whitespace-pre-wrap">{c.body}</p>
      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={copy} className="flex-1 h-9 rounded-full text-[12px] border-[#c7d2fe] bg-white text-[#4338ca]" data-testid={`comm-copy-${c.id}`}>
          <Copy size={13} className="mr-1.5"/>Copiar
        </Button>
        <Button onClick={ack} disabled={busy} className="flex-1 h-9 rounded-full text-[12px] bg-[#4338ca] text-white hover:bg-[#3730a3]" data-testid={`comm-ack-${c.id}`}>
          {busy ? <Loader2 size={13} className="mr-1.5 animate-spin"/> : <CheckCircle2 size={13} className="mr-1.5"/>}Confirmar lectura
        </Button>
      </div>
    </div>
  );
};

const MobilePushes = () => {
  const [items, setItems] = useState([]);
  const [comms, setComms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/my-pending-pushes').then(r => setItems(r.data)).finally(() => setLoading(false));
    api.get('/comms/mine').then(r => setComms(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, []);

  return (
    <div className="p-4 space-y-4 pb-6" data-testid="mobile-pushes-page">
      <header className="space-y-1">
        <h1 className="font-serif text-[28px] leading-tight tracking-tight">Cambios de precio</h1>
        {isGtFullServiceDay() && (
          <div className="mt-3 bg-[#dc2626] text-white rounded-xl p-4 text-center shadow-lg shadow-red-900/20" data-testid="full-service-day-banner-home">
            <p className="text-[15px] font-bold uppercase tracking-wide">Hoy es día de servicio completo</p>
            <p className="text-[12px] mt-1 opacity-95">Día 1 del mes: en los trackings captura AMBOS servicios (Auto y Full) donde aplique. El 2º tracking del día NO se guardará sin completar 1 tracking de servicio completo.</p>
          </div>
        )}
        <p className="text-[12px] text-[#5a5a5a]">Confirma recepción 2 h antes y luego sube la foto del monolito.</p>
      </header>

      {comms.map(c => <CommCard key={c.id} c={c} onAck={(id) => setComms(cs => cs.filter(x => x.id !== id))}/>)}

      {loading ? <Loader2 className="animate-spin text-[#8a8579] mx-auto"/> : items.length === 0 ? (
        <div className="bg-white border border-[#e8e5de] rounded-xl p-8 text-center">
          <AlertCircle size={28} className="mx-auto text-[#8a8579]"/>
          <p className="text-[13px] text-[#5a5a5a] mt-2">No tienes cambios de precio pendientes.</p>
        </div>
      ) : items.map(sp => <PushCard key={sp.id} sp={sp} onRefresh={load}/>)}

      <HappyHourCards/>

      {/* Alertas (movidas desde MO-P01 "Monitoreo") */}
      <div className="pt-2 border-t border-[#e8e5de]"/>
      <NotificationsFeed />
    </div>
  );
};

export default MobilePushes;
